import { dateTimeFormat, formatToRunningCase, getDecimalNumber } from "src/utils";
import styles from "./walletReportsContent.module.scss";
import { EMPTY_STRING, groupTypeMapping, roleMapping, walletTypeMapping } from "src/components/constant/constant";
import { bgColorbyName, groupColors, organisationTypeColors, roleColors, typeColors } from "src/variable/randomColor";
import { Link } from "react-router-dom";
import { Avatar } from "@mui/material";
import { ReportsContent } from "../reportsContent/reportsContentSlice";

const columns = [
  {
    title: "Id",
    dataIndex: "object_id",
    key: "object_id"
  },
  {
    title: "Organisation",
    dataIndex: "data",
    key: "organisation_id",
    render: (data: { organisation_id: string; organisation_name: string; organisation_type: string }) => (
      <Link to={`/account/${data.organisation_id}`} className={styles.tableMember}>
        <div className={styles.tableMemberImg}>
          <Avatar
            id="av"
            aria-label="Member"
            style={{ backgroundColor: bgColorbyName(data.organisation_name ?? EMPTY_STRING) }}
          >
            {data.organisation_name?.charAt(0).toLocaleUpperCase()}
          </Avatar>
        </div>
        <div className={styles.tableMemberDetail}>
          <div className={styles.tableMemberName}>{data.organisation_name}</div>
          <div className={styles.tableMemberEmail}>{data.organisation_type}</div>
        </div>
      </Link>
    )
  },
  {
    title: "Date",
    dataIndex: "report_date",
    key: "report_date",
    render: (report_date: string) => <div>{dateTimeFormat(report_date).date}</div>
  },
  {
    title: "Card",
    dataIndex: "data",
    key: "wallet",
    render: (data: { wallet_id: string; wallet_name: string; wallet_type: string; wallet_status: string }) => (
      <Link to={`/wallets/${data.wallet_id}`} className={styles.tableMember}>
        <div className={styles.tableMemberImg}>
          <Avatar
            id="av"
            aria-label="Member"
            style={{ backgroundColor: bgColorbyName(data.wallet_name ?? EMPTY_STRING) }}
          >
            {data.wallet_name?.charAt(0).toLocaleUpperCase()}
          </Avatar>
        </div>
        <div className={styles.tableMemberDetail}>
          <div className={styles.tableMemberName}>{data.wallet_name}</div>
          <div className={styles.typeWrapper}>
            <div
              className={styles.type}
              style={{
                backgroundColor: `${typeColors[data.wallet_type]}0d`,
                borderColor: `${typeColors[data.wallet_type]}73`,
                color: `${typeColors[data.wallet_type]}FF`
              }}
            >
              {walletTypeMapping[data.wallet_type]}
            </div>
            <div className={styles.divider}></div>
            <div className={`${styles.status} ${data.wallet_status === "ACTIVE" ? styles.green : styles.grey}`}>
              {data.wallet_status === "ACTIVE" ? "Active" : "Archived"}
            </div>
          </div>
        </div>
      </Link>
    )
  },
  {
    title: "Owner",
    dataIndex: "data",
    key: "owner",
    render: (
      data: {
        owner: { owner_id: string; owner_name: string; owner_description: string; owner_type: string };
      },
      record: ReportsContent
    ) => {
      let link = EMPTY_STRING;
      let role: { name: string; color: string; bgColor: string; borderColor: string } = {
        name: EMPTY_STRING,
        color: EMPTY_STRING,
        bgColor: EMPTY_STRING,
        borderColor: EMPTY_STRING
      };

      switch (record.object_type) {
        case "USER":
          link = `/users/${data.owner.owner_id}`;
          role = {
            name: roleMapping[data.owner.owner_type],
            bgColor: `${roleColors[data.owner.owner_type]}0d`,
            color: `${roleColors[data.owner.owner_type]}FF`,
            borderColor: `${roleColors[data.owner.owner_type]}73`
          };
          break;

        case "GROUP":
          link = `/groups/${data.owner.owner_id}`;
          role = {
            name: groupTypeMapping[data.owner.owner_type],
            color: `${groupColors[data.owner.owner_type]}FF`,
            bgColor: `${groupColors[data.owner.owner_type]}0d`,
            borderColor: `${groupColors[data.owner.owner_type]}73`
          };
          break;

        case "ORGANISATION": {
          link = `/account/${data.owner.owner_id}`;
          const organisationType = data.owner.owner_type;
          const organisationColor = organisationTypeColors[organisationType] || "#64748b";

          role = {
            name: formatToRunningCase(organisationType),
            color: `${organisationColor}FF`,
            bgColor: `${organisationColor}0d`,
            borderColor: `${organisationColor}73`
          };
          break;
        }

        default:
          link = "#";
          role = {
            name: "Unknown",
            color: "#808080FF",
            bgColor: "#8080800d",
            borderColor: "#80808073"
          };
          break;
      }
      return (
        <Link to={link} className={styles.tableOwner}>
          <div className={styles.tableOwnerImg}>
            <Avatar
              id="av"
              aria-label="Member"
              style={{ backgroundColor: bgColorbyName(data.owner.owner_name ?? EMPTY_STRING) }}
            >
              {data.owner.owner_name?.charAt(0).toLocaleUpperCase()}
            </Avatar>
          </div>
          <div className={styles.tableOwnerDetail}>
            <div className={styles.tableOwnerName}>{data.owner.owner_name}</div>
            <div className={styles.tableOwnerEmail}>{data.owner.owner_description}</div>
          </div>
          <div className={styles.tableOwnerRole}>
            <div
              className={styles.tableOwnerRoleName}
              style={{ backgroundColor: role.bgColor, borderColor: role.borderColor, color: role.color }}
            >
              {role.name}
            </div>
          </div>
        </Link>
      );
    }
  },
  {
    title: "Initial Balance",
    dataIndex: "data",
    key: "initial_balance",
    render: (data: { initial_balance: number }) => <div>${data?.initial_balance?.toFixed(2) || "0.00"}</div>
  },
  {
    title: "End Balance",
    dataIndex: "data",
    key: "end_balance",
    render: (data: { end_balance: number }) => <div>${data?.end_balance?.toFixed(2) || "0.00"}</div>
  },
  {
    title: "Net Change",
    dataIndex: "data",
    key: "net_change",
    render: (data: { net_change: number }) => (
      <div
        style={{
          color: data?.net_change >= 0 ? "#28a745" : "#dc3545",
          fontWeight: 600
        }}
      >
        ${getDecimalNumber(data?.net_change) || "0.00"}
      </div>
    )
  },
  {
    title: "Transaction Count",
    dataIndex: "data",
    key: "total_transactions",
    render: (data: { total_transactions: number }) => <div>{data?.total_transactions || 0}</div>
  },
  {
    title: "Total Card Transactions",
    dataIndex: "data",
    key: "total_card_transactions",
    render: (data: { total_card_transactions: number }) => <div>{data?.total_card_transactions || 0}</div>
  },
  {
    title: "Total Card Transactions",
    dataIndex: "data",
    key: "total_wallet_transactions",
    render: (data: { total_wallet_transactions: number }) => <div>{data?.total_wallet_transactions || 0}</div>
  },
  {
    title: "Total Inflow",
    dataIndex: "data",
    key: "total_inflow",
    render: (data: { total_inflow: number }) => (
      <div style={{ color: "#28a745", fontWeight: 600 }}>${getDecimalNumber(data?.total_inflow) || "0.00"}</div>
    )
  },
  {
    title: "Total Outflow",
    dataIndex: "data",
    key: "total_outflow",
    render: (data: { total_outflow: number }) => (
      <div style={{ color: "#dc3545", fontWeight: 600 }}>${getDecimalNumber(data?.total_outflow) || "0.00"}</div>
    )
  },
  {
    title: "Min Transaction",
    dataIndex: "data",
    key: "min_transaction_amount",
    render: (data: { min_transaction_amount?: number }) => (
      <div>
        {data?.min_transaction_amount !== undefined ? `$${getDecimalNumber(data.min_transaction_amount)}` : "-"}
      </div>
    )
  },
  {
    title: "Max Transaction",
    dataIndex: "data",
    key: "max_transaction_amount",
    render: (data: { max_transaction_amount?: number }) => (
      <div>
        {data?.max_transaction_amount !== undefined ? `$${getDecimalNumber(data.max_transaction_amount)}` : "-"}
      </div>
    )
  }
];

interface WalletReportData {
  wallet_id: string;
  wallet_name?: string;
  wallet_type?: string;
  wallet_status?: string;
  owner: { owner_id: string; owner_name: string; owner_description: string; owner_type: string };
  organisation_id: string;
  organisation_name: string;
  organisation_type: string;
  initial_balance: number;
  end_balance: number;
  net_change: number;
  total_transactions: number;
  total_card_transactions: number;
  total_wallet_transactions: number;
  total_inflow: number;
  total_outflow: number;
  min_transaction_amount?: number;
  max_transaction_amount?: number;
  transaction_ids: string[];
}

export const convertToCSV = (data: ReportsContent[]) => {
  const headers = [
    "Report Content Id",
    "Organisation_ID",
    "Organisation Name",
    // "Organisation Type",
    "Wallet ID",
    "Wallet Name",
    // "Wallet Type",
    "Wallet Status",
    "Owner ID",
    "Owner Name",
    "Owner Description",
    "Owner Type",
    "Initial Balance",
    "End Balance",
    "Net Change",
    "Transaction Count",
    "Total Card Transactions",
    "Total Wallet Transactions"
    // "Total Inflow",
    // "Total Outflow",
    // "Min Transaction Amount",
    // "Max Transaction Amount",
    // "Transaction IDs"
  ];

  const rows = data.map((item) => {
    const dataObj = item.data as WalletReportData;

    return [
      item.object_id,
      dataObj.organisation_id || EMPTY_STRING,
      dataObj.organisation_name || EMPTY_STRING,
      // dataObj.organisation_type || EMPTY_STRING,
      dataObj.wallet_id || EMPTY_STRING,
      dataObj.wallet_name || EMPTY_STRING,
      // dataObj.wallet_type || EMPTY_STRING,
      dataObj.wallet_status || EMPTY_STRING,
      dataObj.owner.owner_id || EMPTY_STRING,
      dataObj.owner.owner_name || EMPTY_STRING,
      dataObj.owner.owner_description || EMPTY_STRING,
      dataObj.owner.owner_type || EMPTY_STRING,
      getDecimalNumber(dataObj.initial_balance) || 0,
      getDecimalNumber(dataObj.end_balance) || 0,
      dataObj.net_change.toFixed(3) || 0,
      dataObj.total_transactions || 0,
      dataObj.total_card_transactions || 0,
      dataObj.total_wallet_transactions || 0
      // getDecimalNumber(dataObj.total_inflow) || 0,
      // getDecimalNumber(dataObj.total_outflow) || 0,
      // dataObj.min_transaction_amount !== undefined ? getDecimalNumber(dataObj.min_transaction_amount) : EMPTY_STRING,
      // dataObj.max_transaction_amount !== undefined ? getDecimalNumber(dataObj.max_transaction_amount) : EMPTY_STRING,
      // dataObj.transaction_ids?.join(",") || EMPTY_STRING
    ];
  });

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, "EMPTY_STRING")}"`).join(","))
  ].join("\n");

  return csvContent;
};

export default columns;
